import { createRouter, createWebHistory } from 'vue-router';
import { brand } from '@/config/brand';
import { accountDestination } from '@/router/access';
import { useAuthStore } from '@/stores/auth';
import { useWorkspaceStore } from '@/stores/workspace';

const routes = [
  {
    path: '/',
    name: 'home',
    redirect: { name: 'dashboard' },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { title: 'Sign in', guestOnly: true },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/auth/RegisterView.vue'),
    meta: { title: 'Create account', guestOnly: true },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/auth/ForgotPasswordView.vue'),
    meta: { title: 'Forgot password', guestOnly: true },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/auth/ResetPasswordView.vue'),
    meta: { title: 'Reset password', guestOnly: true },
  },
  {
    path: '/oauth/google/callback',
    name: 'oauth-google-callback',
    component: () => import('@/views/auth/OAuthCallbackView.vue'),
    meta: { title: 'Google sign-in' },
  },
  {
    path: '/accept-invitation',
    name: 'accept-invitation',
    component: () => import('@/views/auth/AcceptInvitationView.vue'),
    meta: { title: 'Accept invitation' },
  },
  {
    path: '/verify-email',
    name: 'verify-email',
    component: () => import('@/views/auth/VerifyEmailView.vue'),
    meta: { title: 'Verify email', requiresAuth: true },
  },
  {
    path: '/pending-approval',
    name: 'pending-approval',
    component: () => import('@/views/auth/PendingApprovalView.vue'),
    meta: { title: 'Approval pending', requiresAuth: true, requiresVerified: true },
  },
  {
    path: '/account-unavailable',
    name: 'account-unavailable',
    component: () => import('@/views/auth/AccountUnavailableView.vue'),
    meta: { title: 'Account unavailable', requiresAuth: true },
  },
  {
    path: '/onboarding/workspace',
    name: 'workspace-onboarding',
    component: () => import('@/views/onboarding/WorkspaceOnboardingView.vue'),
    meta: {
      title: 'Create workspace',
      requiresAuth: true,
      requiresVerified: true,
      requiresActive: true,
      workspaceOnboarding: true,
    },
  },
  {
    path: '/f/:slug',
    name: 'public-form',
    component: () => import('@/views/forms/PublicFormView.vue'),
    meta: { title: 'Form', public: true },
  },
  {
    path: '/forms/:formId/edit',
    name: 'form-editor',
    component: () => import('@/views/forms/FormBuilderView.vue'),
    meta: {
      title: 'Form editor',
      requiresAuth: true,
      requiresVerified: true,
      requiresActive: true,
      requiresWorkspace: true,
    },
  },
  {
    path: '/forms/:formId/results',
    name: 'form-results',
    component: () => import('@/views/forms/FormResultsView.vue'),
    meta: {
      title: 'Form results',
      requiresAuth: true,
      requiresVerified: true,
      requiresActive: true,
      requiresWorkspace: true,
    },
  },
  {
    path: '/team',
    name: 'team',
    component: () => import('@/views/TeamView.vue'),
    meta: {
      title: 'Team',
      requiresAuth: true,
      requiresVerified: true,
      requiresActive: true,
      requiresWorkspace: true,
    },
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: {
      title: 'Forms',
      requiresAuth: true,
      requiresVerified: true,
      requiresActive: true,
      requiresWorkspace: true,
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { title: 'Page not found' },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  const workspace = useWorkspaceStore();

  if (to.meta.public) return true;

  try {
    await auth.initialize();
  } catch {
    if (to.meta.requiresAuth) {
      return { name: 'login' };
    }
  }

  if (to.meta.guestOnly && auth.user) {
    return accountDestination(auth.user);
  }

  if (to.meta.requiresAuth && !auth.user) {
    return {
      name: 'login',
      query: { redirect: to.fullPath },
    };
  }

  if (to.meta.requiresVerified && !auth.isVerified) {
    return { name: 'verify-email' };
  }

  if (to.meta.requiresActive && !auth.isActive) {
    return accountDestination(auth.user);
  }

  if (auth.user && auth.isVerified && auth.isActive) {
    await workspace.initialize();

    if (to.meta.requiresWorkspace && !workspace.hasWorkspace) {
      return { name: 'workspace-onboarding' };
    }

    if (to.meta.workspaceOnboarding && workspace.hasWorkspace) {
      return { name: 'dashboard' };
    }
  }

  return true;
});

router.afterEach((to) => {
  document.title = to.meta.title
    ? to.meta.title + ' — ' + brand.applicationName
    : brand.applicationName;
});

export default router;
